import { Box, Button, Typography } from "@mui/material";
import { StyledCreateQuotePage } from "./createQuote.styled";
import { useContext, useEffect, useState } from "react";
import { DocumentContext } from "../../../globals/LanguageContext";
import { CustomerInfoCard } from "../../../components/customerInfoCard/CustomerInfoCard";
import { Contact } from "../../../models/Contact";
import { CompanyInfoCard } from "../../../components/companyInfoCard/CompanyInfoCard";
import { PlusIcon } from "@phosphor-icons/react";
import { Quote } from "../../../models/Quote";
import { Project } from "../../../models/Project";
import {
  formatDateToDDMMYYYY,
  formatDateToYYYYMM,
} from "../../../globals/utils";
import { QuoteItem } from "../../../models/QuoteItem";
import { QuoteItemCard } from "../../../components/quoteItemCard/QuoteItemCard";
import { DarkOverlay } from "../../../components/darkOverlay/DarkOverlay";
import { QuoteItemForm } from "../../../components/QuoteItemForm/QuoteItemForm";
import { useParams } from "react-router-dom";
import { useProject } from "../../../stores/useProject";
import { useProfile } from "../../../stores/useProfile";
import { useQuote } from "../../../stores/useQuote";
import { AddQuoteItemDto } from "../../../models/dto/QuoteDTO";

export const CreateQuotePage = () => {
  const { quoteId } = useParams<string>();
  const { documentDictionary } = useContext(DocumentContext);
  const [quote, setQuote] = useState<Quote | undefined>();
  const { projects, fetchProject } = useProject();
  const { addQuoteItem, fetchQuote, removeQuoteItem, updateQuoteItem } =
    useQuote();
  const [darkOverlayOpened, setDarkOverlayOpened] = useState<boolean>(false);
  const [selectedQuoteItem, setSelectedQuoteItem] = useState<QuoteItem>();

  const { user } = useProfile();

  useEffect(() => {
    updateItems();
  }, [quoteId, user]);

  const updateItems = async () => {
    if (!user || !quoteId) return;

    const res = await fetchQuote(user, Number(quoteId));

    if (res && res.status === 200) setQuote(res.data);
  };

  const handleAddQuoteItem = async (newQuoteItem: AddQuoteItemDto) => {
    console.log(user, quote);
    if (!user || !quote) return;

    const res = await addQuoteItem(user, quote.id, newQuoteItem);

    if (res === 201) {
      updateItems();
      setDarkOverlayOpened(false);
      setSelectedQuoteItem(undefined);
    }
  };

  const handleQuoteItemEdit = (quoteItem: QuoteItem) => {
    setSelectedQuoteItem(quoteItem);
    setDarkOverlayOpened(true);
  };

  const handleQuoteItemRemove = async (quoteItemId: number) => {
    if (!user) return;

    const res = await removeQuoteItem(user, quote?.id!, quoteItemId);

    if (res) {
      setSelectedQuoteItem(undefined);
      updateItems();
    }
  };

  const handleUpdateQuoteItem = async (
    quoteItemId: number,
    quoteItem: AddQuoteItemDto
  ) => {
    if (!user) return;

    const res = await updateQuoteItem(user, quote?.id!, quoteItemId, quoteItem);

    if (res) {
      setSelectedQuoteItem(undefined);
      setDarkOverlayOpened(false);
      updateItems();
    }
  };

  return (
    <>
      {quote ? (
        <StyledCreateQuotePage>
          <DarkOverlay
            opened={darkOverlayOpened}
            component={
              <QuoteItemForm
                quoteItem={selectedQuoteItem}
                onSave={(data) => handleAddQuoteItem(data)}
                onUpdate={(quoteItemId, data) =>
                  handleUpdateQuoteItem(quoteItemId, data)
                }
              />
            }
            onClose={() => {
              setDarkOverlayOpened(false);
              setSelectedQuoteItem(undefined);
            }}
          />
          <Box className="quoteWrapper">
            <Box className="quoteHeader">
              {quote.contact && (
                <CustomerInfoCard customerInfo={quote.contact} />
              )}
              <CompanyInfoCard title={documentDictionary.quote.title} />
            </Box>
            <Box className="quoteData">
              <Box className="head">
                <Typography variant="h4">
                  {documentDictionary.quote.quoteDate}
                </Typography>
                <Typography variant="h4">
                  {documentDictionary.quote.quoteNumber}
                </Typography>
                <Typography variant="h4" />
                <Typography variant="h4">
                  {documentDictionary.quote.validUntill}
                </Typography>
              </Box>
              <Box className="data">
                <Typography variant="h4">
                  {quote ? formatDateToDDMMYYYY(quote.quoteDate) : "--/--/----"}
                </Typography>
                <Typography variant="h4">{quote.code}</Typography>
                <Typography variant="h4" />
                <Typography variant="h4">
                  {quote ? formatDateToDDMMYYYY(quote.validDate) : "--/--/----"}
                </Typography>
              </Box>
            </Box>
            <Box className="quoteItems">
              <Box className="head">
                <Typography variant="h4" className="position">
                  {documentDictionary.quote.quoteItems.position}
                </Typography>
                <Typography variant="h4" className="description">
                  {documentDictionary.quote.quoteItems.description}
                </Typography>
                <Typography variant="h4" className="total">
                  {documentDictionary.quote.quoteItems.total}
                </Typography>
              </Box>
              {quote &&
                quote.quoteItems.map((quoteItem, index) => (
                  <QuoteItemCard
                    quoteItem={quoteItem}
                    position={index}
                    onEdit={handleQuoteItemEdit}
                    onRemove={handleQuoteItemRemove}
                  />
                ))}
              <Button onClick={() => setDarkOverlayOpened(true)}>
                <PlusIcon size={32} weight="bold" />
              </Button>
            </Box>
            <Box className="quoteInfo">
              <Box className="paymentTerms">
                <Typography variant="h4" className="title">{documentDictionary.quote.paymentConditions.title}</Typography>

                <Box className="row">
                  <Box className="column number">
                    <Typography variant="h4">50%</Typography>
                  </Box>
                  <Box className="column">
                    <Typography variant="h4">Bij akkoord offerte</Typography>
                  </Box>
                </Box>
                <Box className="row">
                  <Box className="column number">
                    <Typography variant="h4">25%</Typography>
                  </Box>
                  <Box className="column">
                    <Typography variant="h4">Na ruwbouwwerken</Typography>
                  </Box>
                </Box>
                <Box className="row">
                  <Box className="column number">
                    <Typography variant="h4">25%</Typography>
                  </Box>
                  <Box className="column">
                    <Typography variant="h4">Saldo</Typography>
                  </Box>
                </Box>
              </Box>
              <Box className="total">
                <Box className="row">
                  <Typography variant="h4">
                    {documentDictionary.quote.exVat}:
                  </Typography>
                  <Typography variant="h4">
                    {Number(quote.priceExVat).toFixed(2)}
                  </Typography>
                </Box>
                <Box className="row">
                  <Typography variant="h4">
                    {documentDictionary.quote.vat} [{quote.globalVat}%]:
                  </Typography>
                  <Typography variant="h4">
                    {Number(quote.vat).toFixed(2)}
                  </Typography>
                </Box>
                <Box className="row totalRow">
                  <Typography variant="h3">
                    {documentDictionary.quote.total}:
                  </Typography>
                  <Typography variant="h3">
                    {Number(quote.total).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </StyledCreateQuotePage>
      ) : (
        <></>
      )}
    </>
  );
};
